import { motion } from "framer-motion";
import { DraftingCompass, Wrench, Flag, Sprout, BatteryCharging, Gauge, Radio, ShieldCheck } from "lucide-react";
import SectionHead from "./SectionHead";
import { fadeUp, stagger, sectionPad } from "./motion";
import { useLang } from "../../i18n/LanguageContext";

const PILLAR_ICONS = [DraftingCompass, Wrench, Flag, Sprout];
const SPEC_ICONS = [Gauge, BatteryCharging, Radio, ShieldCheck];

const TEAM_IMG = "/img/build.jpg";
const COCKPIT_IMG =
  "https://images.unsplash.com/photo-1638729529430-c4307b3616ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxlbmdpbmVlcmluZyUyMHN0dWRlbnRzJTIwdGVsZW1ldHJ5JTIwcmFjaW5nJTIwY2FyJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3OTE1NTAwMDB8MA&ixlib=rb-4.1.0&q=85";

export default function Progetto() {
  const { t } = useLang();
  const c = t.progetto;

  return (
    <section id="progetto" className={`relative bg-vg-black ${sectionPad}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead index="01" kicker={c.kicker} title={c.title} en={c.en} />

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.pillars.map((p, i) => (
            <motion.article
              key={p.t}
              variants={fadeUp}
              className="group relative overflow-hidden border border-white/10 bg-vg-card p-8 transition-colors duration-300 hover:border-vg-green/40"
              data-testid={`pillar-0${i + 1}`}
            >
              <span className="text-outline font-display text-6xl font-bold" aria-hidden="true">
                0{i + 1}
              </span>
              {PILLAR_ICONS[i] && (() => { const Icon = PILLAR_ICONS[i]; return <Icon className="mt-6 h-7 w-7 text-vg-green" />; })()}
              <h3 className="mt-4 font-display text-2xl font-semibold uppercase tracking-wider text-vg-white">{p.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-vg-muted">{p.d}</p>
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-vg-green transition-all duration-300 group-hover:w-full" aria-hidden="true" />
            </motion.article>
          ))}
        </motion.div>

        <div className="mt-16 grid gap-6 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="clip-frame relative min-h-[300px] overflow-hidden border border-white/10 lg:col-span-5"
          >
            <img src={TEAM_IMG} alt="Studenti al lavoro sul prototipo elettrico nel laboratorio d'istituto" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-vg-black/85 via-transparent to-transparent" aria-hidden="true" />
            <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.2em] text-vg-green">{c.imgCaption}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="border border-white/10 bg-vg-asphalt p-8 sm:p-10 lg:col-span-7"
            data-testid="scheda-tecnica"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-vg-green">{c.specEyebrow}</p>
            <h3 className="mt-3 font-display text-3xl font-bold uppercase tracking-wide text-vg-white sm:text-4xl">{c.specTitle}</h3>

            <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
              {c.specs.map((s, i) => (
                <div key={s.label} className="flex items-start gap-5 py-5" data-testid={`spec-${s.label.toLowerCase()}`}>
                  {SPEC_ICONS[i] && (() => { const Icon = SPEC_ICONS[i]; return <Icon className="mt-1 h-5 w-5 shrink-0 text-vg-green" />; })()}
                  <span className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted sm:w-36 sm:text-[11px]">{s.label}</span>
                  <span className="font-display text-lg font-medium uppercase tracking-wide text-vg-white sm:text-xl">{s.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-5">
              <div className="flex flex-wrap items-center gap-3 sm:col-span-3">
                {c.chips.map((chip) => (
                  <span key={chip} className="hud-chip">{chip}</span>
                ))}
              </div>
              <div className="clip-frame relative h-36 overflow-hidden border border-white/10 sm:col-span-2 sm:h-auto">
                <img src={COCKPIT_IMG} alt="Telemetria del veicolo EVGP" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-vg-black/40" aria-hidden="true" />
                <p className="absolute bottom-3 left-3 font-mono text-[9px] uppercase tracking-[0.2em] text-vg-white">{c.cockpitCaption}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
